/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-object-type */
// 2) Third-party library imports
import {
    concat,
    get,
    isEqual,
    isUndefined,
    keyBy,
    merge,
    uniqWith,
    values,
} from 'lodash-es';
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';

// 3a) Local imports � Constants
import { PAYMENTS_PAGE, DETAILS_TAB } from '../../config/tableAndPageConstants';
import { conversationTableFilterOptions } from '../../constants/common';
import { invoicesSortByOptions, invoicesStateFilterOptions } from '../../constants/invoicesSortAndFilters';
import {
    paymentsSortByOptions,
    paymentChangesTableFilterOptions,
    paymentStateFilterOptions,
} from '../../constants/paymentsSortAndFilters';
import { salesOrdersSortByOptions, salesOrdersStateFilterOptions } from '../../constants/salesOrdersSortAndFilters';

// 3c) Local imports � Project-specific modules / types
import {
    AddPaymentForADVAMRequestPayload,
    AddPaymentForConveraRequestPayload,
    AddPaymentForCorpayRequestPayload,
    AddPaymentForEziDebitRequestPayload,
    AddPaymentForIntegraPayRequestPayload,
    AddPaymentForUrlRequestPayload,
    AddPaymentForWesternUnionRequestPayload,
    AddPaymentForXeroRequestPayload,
    GetPaymentChangesRequestPayload,
    GetPaymentsRequestPayload,
    Payment,
    PaymentAddCommentRequestPayload,
    PaymentChanges,
    PaymentsState,
    SendPaymentInstructionForCorpayRequestPayload,
} from './types';
import { PageData, Conversation, GetConversationRequestPayload } from '../common/types';
import { GetInvoicesRequestPayload, Invoice } from '../invoices/types';
import { GetSalesOrdersRequestPayload, SalesOrder } from '../salesOrders/types';
import type { RootState } from '../storeTypes';

// 3d) Local imports � Utility functions / interfaces
import { DynamicObject } from '../../utils/commonInterfaces';

const actionPrefix = 'payment';

// Type-safe initialState!
export const initialState: PaymentsState = {
    loading: false,
    savePaymentDataLoading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: PAYMENTS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {},
    ActiveState: get(paymentStateFilterOptions, `${0}.value`),
    tableFilter: undefined,
    sortBy: paymentsSortByOptions[2].value, //2 for payment date
    sortAscending: false, // Descending order by default
    bankFileFormats: [],
    activeData: {
        record: {},
        invoices: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            invoiceState: get(invoicesStateFilterOptions, `${1}.value`),
            sortBy: invoicesSortByOptions[0].value, // 2 for Invoice number
            sortAscending: true,
        },
        salesOrders: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.SALES_ORDER_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                SalesOrderNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            salesOrderState: get(salesOrdersStateFilterOptions, `${1}.value`),
            sortBy: salesOrdersSortByOptions[0].value,
            sortAscending: true,
        },
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        paymentChanges: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            paymentChangesTableFilter: get(
                paymentChangesTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        loading: false,
        selectedId: null,
        errorMessages: [],
    },
};

const paymentSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getPaymentsRequestAction: (
            state,
            _action: PayloadAction<GetPaymentsRequestPayload>
        ) => {
            state.loading = true;
        },
        getPaymentsSuccessAction: (
            state,
            action: PayloadAction<{
                data: Payment[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                const existingData = state.data || [];
                state.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = initialState.errorMessages;
        },
        getPaymentsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        updatePaymentsFiltersAction: (
            state,
            action: PayloadAction<GetPaymentsRequestPayload['filters']>
        ) => {
            state.filters = action.payload;
        },
        updatePaymentsSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetPaymentsRequestPayload['sortBy'];
                sortAscending: boolean;
                activeState: string;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
            state.ActiveState = action.payload.activeState;
        },
        updatePaymentsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        clearPaymentsStateAllTableFiltersAction: (state) => {
            state.filters = initialState.filters;
            state.sortBy = initialState.sortBy;
            state.sortAscending = initialState.sortAscending;
            state.tableFilter = initialState.tableFilter;
            state.ActiveState = initialState.ActiveState;
        },
        clearPaymentsStateDataAction: (state) => {
            Object.assign(state, initialState);
        },
        setPaymentSelectedIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    paymentId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                paymentId: string | null,
                callback: () => void
            ) => ({
                payload: {
                    paymentId,
                    callback
                }
            })
        },
        setPaymentSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getPaymentDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    paymentId: string;
                    isUsingCloudImportType: boolean;
                    companyId: string | null;
                }>
            ) => {
                state.activeData.record = initialState.activeData.record;
                state.activeData.loading = true;
            },
            prepare: (
                paymentId: string,
                isUsingCloudImportType: boolean,
                companyId: string | null = null
            ) => ({
                payload: {
                    paymentId,
                    isUsingCloudImportType,
                    companyId
                }
            })
        },
        getPaymentDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = initialState.activeData.errorMessages;
        },
        getPaymentDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        getBankFileFormatsRequestAction: (state) => {
            state = initialState;
        },
        getBankFileFormatsSuccessAction: (
            state,
            action: PayloadAction<{ bankFileFormats: [] }>
        ) => {
            state.bankFileFormats = action.payload.bankFileFormats;
        },
        getBankFileFormatsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearPaymentDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        getPaymentInvoicesRequestAction: (
            state,
            _action: PayloadAction<GetInvoicesRequestPayload>
        ) => {
            state.activeData.invoices.loading = true;
        },
        getPaymentInvoicesSuccessAction: (
            state,
            action: PayloadAction<{
                data: Invoice[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.invoices.data = action.payload.data;
            } else {
                const existingData = state.activeData.invoices.data || [];
                state.activeData.invoices.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.loading = false;
            state.activeData.invoices.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = initialState.activeData.invoices.errorMessages;
        },
        getPaymentInvoicesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.invoices.loading = false;
            state.activeData.invoices.data = initialState.activeData.invoices.data;
            state.activeData.invoices.errorMessages = action.payload;
        },
        updatePaymentInvoicesFiltersAction: (
            state,
            action: PayloadAction<GetInvoicesRequestPayload['filters']>
        ) => {
            state.activeData.invoices.filters = action.payload;
        },
        updatePaymentInvoicesSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetInvoicesRequestPayload['sortBy'];
                sortAscending: boolean;
                invoiceState: string;
            }>
        ) => {
            state.activeData.invoices.sortBy = action.payload.sortBy;
            state.activeData.invoices.sortAscending = action.payload.sortAscending;
            state.activeData.invoices.invoiceState = action.payload.invoiceState;
        },
        updatePaymentInvoicesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.invoices.invoiceState = action.payload;
        },
        getPaymentSalesOrdersRequestAction: (
            state,
            action: PayloadAction<GetSalesOrdersRequestPayload>
        ) => {
            state.activeData.salesOrders.loading = true;
        },
        getPaymentSalesOrdersSuccessAction: (
            state,
            action: PayloadAction<{
                data: SalesOrder[];
                pageData: PageData;
            }>
        ) => {
            const { data, pageData } = action.payload;

            let newDataState: SalesOrder[];

            if (pageData.currentPage === 0) {
                newDataState = data;
            } else {
                newDataState = uniqWith(concat(state.activeData.salesOrders.data, data), isEqual);
            }

            state.activeData.salesOrders.loading = false;
            state.activeData.salesOrders.data = newDataState;
            state.activeData.salesOrders.pageData = pageData;
            state.activeData.salesOrders.errorMessages = initialState.activeData.salesOrders.errorMessages;
        },
        getPaymentSalesOrdersErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.salesOrders.loading = false;
            state.activeData.salesOrders.data = initialState.activeData.salesOrders.data;
            state.activeData.salesOrders.errorMessages = action.payload;
        },
        updatePaymentSalesOrdersFiltersAction: (
            state,
            action: PayloadAction<GetSalesOrdersRequestPayload['filters']>
        ) => {
            state.activeData.salesOrders.filters = action.payload;
        },
        updatePaymentSalesOrdersSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetSalesOrdersRequestPayload['sortBy'];
                sortAscending: boolean;
                salesOrderState: string;
            }>
        ) => {
            state.activeData.salesOrders.sortBy = action.payload.sortBy;
            state.activeData.salesOrders.sortAscending = action.payload.sortAscending;
            state.activeData.salesOrders.salesOrderState = action.payload.salesOrderState;
        },
        updatePaymentSalesOrdersTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.salesOrders.salesOrderState = action.payload;
        },
        getOrganisationPaymentConversationRequestAction: (
            state,
            _action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getPaymentConversationRequestAction: (
            state,
            _action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getPaymentConversationSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.conversation.data = action.payload.data;
            } else {
                const existingData = state.activeData.conversation.data || [];
                state.activeData.conversation.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.conversation.loading = false;
            state.activeData.conversation.pageData = action.payload.pageData;
            state.activeData.conversation.errorMessages = initialState.activeData.conversation.errorMessages;
        },
        getPaymentConversationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.conversation.loading = false;
            state.activeData.conversation.data = initialState.activeData.conversation.data;
            state.activeData.conversation.errorMessages = action.payload;
        },
        updatePaymentConversationFiltersAction: (
            state,
            action: PayloadAction<GetConversationRequestPayload['filters']>
        ) => {
            state.activeData.conversation.filters = action.payload;
        },
        updatePaymentConversationTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.conversation.conversationTableFilter = action.payload;
        },
        updatePaymentConversationSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetConversationRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.conversation.sortBy = action.payload.sortBy;
            state.activeData.conversation.sortAscending = action.payload.sortAscending;
        },
        getOrganisationPaymentChangesRequestAction: (
            state,
            _action: PayloadAction<GetPaymentChangesRequestPayload>
        ) => {
            state.activeData.paymentChanges.loading = true;
        },
        getPaymentChangesRequestAction: (
            state,
            _action: PayloadAction<GetPaymentChangesRequestPayload>
        ) => {
            state.activeData.paymentChanges.loading = true;
        },
        getPaymentChangesSuccessAction: (
            state,
            action: PayloadAction<{
                data: PaymentChanges[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.paymentChanges.data = action.payload.data;
            } else {
                const existingData = state.activeData.paymentChanges.data || [];
                state.activeData.paymentChanges.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.activeData.paymentChanges.loading = false;

            if (!isUndefined(action.payload.pageData)) {
                state.activeData.paymentChanges.pageData = action.payload.pageData;
            }
            
            state.activeData.paymentChanges.errorMessages = initialState.activeData.paymentChanges.errorMessages;
        },
        getPaymentChangesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.paymentChanges.loading = false;
            state.activeData.paymentChanges.data = initialState.activeData.paymentChanges.data;
            state.activeData.paymentChanges.errorMessages = action.payload;
        },
        updatePaymentChangesFiltersAction: (
            state,
            action: PayloadAction<GetPaymentChangesRequestPayload['filters']>
        ) => {
            state.activeData.paymentChanges.filters = action.payload;
        },
        updatePaymentChangesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.paymentChanges.paymentChangesTableFilter = action.payload;
        },
        updatePaymentChangesSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetPaymentChangesRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.paymentChanges.sortBy = action.payload.sortBy;
            state.activeData.paymentChanges.sortAscending = action.payload.sortAscending;
        },
    }
});

const { actions, reducer } = paymentSlice;

// Actions
export const {
    getPaymentsRequestAction,
    getPaymentsSuccessAction,
    getPaymentsErrorAction,
    updatePaymentsFiltersAction,
    updatePaymentsSortByAndStateAction,
    updatePaymentsTableFilterAction,
    clearPaymentsStateAllTableFiltersAction,
    clearPaymentsStateDataAction,
    setPaymentSelectedIdRequestAction,
    setPaymentSelectedIdSuccessAction,
    getPaymentDataRequestAction,
    getPaymentDataSuccessAction,
    getPaymentDataErrorAction,
    getBankFileFormatsRequestAction,
    getBankFileFormatsSuccessAction,
    getBankFileFormatsErrorAction,
    clearPaymentDataAction,
    getPaymentInvoicesRequestAction,
    getPaymentInvoicesSuccessAction,
    getPaymentInvoicesErrorAction,
    updatePaymentInvoicesFiltersAction,
    updatePaymentInvoicesSortByAndStateAction,
    updatePaymentInvoicesTableFilterAction,
    getPaymentSalesOrdersRequestAction,
    getPaymentSalesOrdersSuccessAction,
    getPaymentSalesOrdersErrorAction,
    updatePaymentSalesOrdersFiltersAction,
    updatePaymentSalesOrdersSortByAndStateAction,
    updatePaymentSalesOrdersTableFilterAction,
    getOrganisationPaymentConversationRequestAction,
    getPaymentConversationRequestAction,
    getPaymentConversationSuccessAction,
    getPaymentConversationErrorAction,
    updatePaymentConversationFiltersAction,
    updatePaymentConversationTableFilterAction,
    updatePaymentConversationSortByAction,
    getOrganisationPaymentChangesRequestAction,
    getPaymentChangesRequestAction,
    getPaymentChangesSuccessAction,
    getPaymentChangesErrorAction,
    updatePaymentChangesFiltersAction,
    updatePaymentChangesTableFilterAction,
    updatePaymentChangesSortByAction
} = actions;

export const addPaymentForUrlRequestAction = createAction(
    `${actionPrefix}/addPaymentForUrlRequestAction`,
    function prepare(
        payload: AddPaymentForUrlRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const addPaymentForWesternUnionRequestAction = createAction(
    `${actionPrefix}/addPaymentForWesternUnionRequestAction`,
    function prepare(
        payload: AddPaymentForWesternUnionRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const addPaymentForEziDebitRequestAction = createAction(
    `${actionPrefix}/addPaymentForEziDebitRequestAction`,
    function prepare(
        payload: AddPaymentForEziDebitRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const addPaymentForIntegraPayRequestAction = createAction(
    `${actionPrefix}/addPaymentForIntegraPayRequestAction`,
    function prepare(
        payload: AddPaymentForIntegraPayRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const addPaymentForADVAMRequestAction = createAction(
    `${actionPrefix}/addPaymentForADVAMRequestAction`,
    function prepare(
        payload: AddPaymentForADVAMRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const addPaymentForCorpayRequestAction = createAction(
    `${actionPrefix}/addPaymentForCorpayRequestAction`,
    function prepare(
        payload: AddPaymentForCorpayRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const getPaymentSpotRateAction = createAction(
    `${actionPrefix}/getPaymentSpotRateAction`,
    function prepare(
        payload: {},
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const bookDealRequestAction = createAction(
    `${actionPrefix}/bookDealRequestAction`,
    function prepare(
        payload: {},
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const sendInstructionPaymentForCorpayRequestAction = createAction(
    `${actionPrefix}/sendInstructionPaymentForCorpayRequestAction`,
    function prepare(
        payload: SendPaymentInstructionForCorpayRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const deallocatePaymentRequestAction = createAction(
    `${actionPrefix}/deallocatePaymentRequestAction`,
    function prepare(
        payload: {},
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const checkLastPaymentChangeLineRequestAction = createAction(
    `${actionPrefix}/checkLastPaymentChangeLineRequestAction`,
    function prepare(
        payload: {},
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { payload, callback },
        };
    }
);

export const getPaymentsCustomFieldsForCompanyRequestAction = createAction(
    `${actionPrefix}/getPaymentsCustomFieldsForCompanyRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: { companyId, callback },
        };
    }
);

export const createCreditAsOverpaymentTasksRequest = createAction(
    `${actionPrefix}/createCreditAsOverpaymentTasksRequest`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const createOrganisationCreditAsOverpaymentTasksRequest = createAction(
    `${actionPrefix}/createOrganisationCreditAsOverpaymentTasksRequest`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const paymentDisregardTasksRequest = createAction(
    `${actionPrefix}/paymentDisregardTasksRequest`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const organisationPaymentDisregardTasksRequest = createAction(
    `${actionPrefix}/organisationPaymentDisregardTasksRequest`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const paymentAddCommentRequestAction = createAction(
    `${actionPrefix}/paymentAddCommentRequestAction`,
    function prepare(
        payload: PaymentAddCommentRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const addPaymentForXeroRequestAction = createAction(
    `${actionPrefix}/addPaymentForXeroRequestAction`,
    function prepare(
        payload: AddPaymentForXeroRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }   
);

export const addPaymentForConveraRequestAction = createAction(
    `${actionPrefix}/addPaymentForConveraRequestAction`,
    function prepare(
        payload: AddPaymentForConveraRequestPayload,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }
);

export const getPaymentsCustomFieldsForOrganisationRequestAction = createAction(
    `${actionPrefix}/getPaymentsCustomFieldsForOrganisationRequestAction`,
    function prepare(
        CompanyIds: string[],
        FieldName?: string[],
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                CompanyIds,
                FieldName,
                callback
            }
        };
    }
);


// Selectors
export const getPaymentsState = (state: RootState) => state.payments;
export const getPaymentBankFileFormats = createSelector(
    getPaymentsState,
    (payments) => payments.bankFileFormats
);
export const getPaymentsPageData = createSelector(
    getPaymentsState,
    (payments) => payments.pageData
);
export const getPaymentData = createSelector(
    getPaymentsState,
    (payments) => payments.activeData
);
export const getPaymentSelectedId = createSelector(
    getPaymentData,
    activeData => activeData.selectedId
);

// Reducer
export default reducer;